import { useRequiredContext } from "@redotech/react-util/context";
import { useTriggerLoad } from "@redotech/react-util/load";
import type { RecognizeEmailStep } from "@redotech/redo-flows-common/advanced-flow-db-parser";
import type { OmsActionStep } from "@redotech/redo-flows-common/steps";
import { StepType } from "@redotech/redo-flows-core-schema-common/common";
import { RedoCustomerRpcClientContext } from "@redotech/redo-merchant-app-common/customer-rpc-client-provider";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { toast } from "@redotech/redo-web/alert";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import type {
  RedoListItem,
  TextRedoListItem,
} from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoMultiselectDropdown } from "@redotech/redo-web/arbiter-components/list/redo-multiselect-dropdown";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { clientErrorMessage } from "@redotech/rpc/errors";
import { memo, useRef, useState } from "react";
import type { PartialWithRequiredType, StepTypeDetailsProps } from ".";
import type { ActionDefinition } from "./action-editor";

type EmailStep = PartialWithRequiredType<RecognizeEmailStep>;

export const RecognizeEmailRecipients = memo(function RecognizeEmailRecipients({
  step,
  setStep,
  readonly,
}: {
  step: EmailStep;
  setStep: (step: EmailStep) => void;
  readonly?: boolean;
}) {
  const team = useRequiredContext(TeamContext);
  const client = useRequiredContext(RedoCustomerRpcClientContext);
  const anchor = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const members: TextRedoListItem<string>[] = team.users.flatMap(({ user }) => {
    if (!user || typeof user === "string" || !user.email) {
      return [];
    }
    return [
      {
        id: user._id.toString(),
        value: user._id.toString(),
        type: "text",
        text: `${user.name} (${user.email})`,
      },
    ];
  });
  const selected = members.filter((member) =>
    step.recipientUserIds?.includes(member.value),
  );
  let buttonText = "Select team members";
  if (selected.length > 0) {
    buttonText = `${selected.length} team members selected`;
  }

  const [testEmail, sendTestEmail] = useTriggerLoad(async () => {
    if (!step.recipientUserIds?.length) {
      return;
    }
    try {
      await client.sendRecognizeTestEmail({
        recipientUserIds: step.recipientUserIds,
      });
      toast("Test email submitted to the email provider", {
        variant: "success",
      });
    } catch (error) {
      toast(clientErrorMessage(error, "Could not send test email"), {
        variant: "error",
      });
    }
  });
  const testEmailDisabled =
    readonly || selected.length === 0 || testEmail.pending;

  return (
    <Flex dir="column" gap="lg" py="lg">
      <Text>
        Send a Recognize notification with the customer, matched VIP criteria,
        and a link to the dashboard.
      </Text>
      <Text>Recipients</Text>
      <RedoButton
        disabled={readonly}
        hierarchy="secondary"
        onClick={() => setOpen(!open)}
        ref={anchor}
        text={buttonText}
      />
      <RedoMultiselectDropdown<string>
        dropdownAnchor={anchor.current}
        dropdownOpen={open}
        dropdownPlacement="bottom-start"
        fitToAnchor
        items={members}
        selectedItems={selected}
        setDropdownOpen={setOpen}
        setSelectedItems={(items) =>
          setStep({
            ...step,
            recipientUserIds: items.map((item) => item.value),
          })
        }
      />
      <RedoButton
        disabled={testEmailDisabled}
        hierarchy="secondary"
        onClick={() => sendTestEmail()}
        pending={testEmail.pending}
        size="sm"
        text="Send test email"
      />
      {selected.map((member) => (
        <Text key={member.value}>{member.text}</Text>
      ))}
    </Flex>
  );
});

const recognizeEmailOption: RedoListItem<string> = {
  id: StepType.RECOGNIZE_EMAIL,
  value: StepType.RECOGNIZE_EMAIL,
  type: "text",
  text: "Email team members",
};

type RecognizeActionStep = PartialWithRequiredType<
  OmsActionStep | RecognizeEmailStep
>;

export function getRecognizeActions({
  step,
  setStep,
  readonly,
}: StepTypeDetailsProps<RecognizeActionStep>): ActionDefinition[] {
  let fields;
  if (step.type === StepType.RECOGNIZE_EMAIL) {
    fields = (
      <RecognizeEmailRecipients
        readonly={readonly}
        setStep={setStep}
        step={step}
      />
    );
  }
  return [
    {
      option: recognizeEmailOption,
      selected: step.type === StepType.RECOGNIZE_EMAIL,
      onSelect() {
        setStep({
          type: StepType.RECOGNIZE_EMAIL,
          id: step.id,
          nextId: step.nextId,
          recipientUserIds: [],
        });
      },
      fields,
    },
  ];
}
